import { useContext, useEffect, useState } from "react";

import { Container, Grid2Props as GridProps } from "@mui/material";

import { GridRenderCellParams } from "@mui/x-data-grid";
import {
  ActionIcon,
  Button,
  CopyButton,
  Divider,
  Modal,
  PasswordInput,
  Popover,
  Select,
  SelectProps,
  Table,
  Text,
  TextInput,
  TextProps,
  Tooltip,
} from "@mantine/core";

import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import {
  IconAt,
  IconEdit,
  IconEye,
  IconKey,
  IconPhone,
  IconPlus,
  IconReload,
  IconTrash,
  IconUser,
  IconX,
} from "@tabler/icons-react";

import dayjs from "dayjs";

import {
  AppDomain,
  AdminRolesList,
  Colors,
  ValidPasswordLength,
  YesOrNoList,
  HttpSuccessCodes,
  HttpSuccessCodesObj,
  HttpErrorCodesObj,
} from "../../Lib/data";
import {
  AdminProfileProps,
  PaginationProps,
  TeacherProps,
} from "../../Lib/types";

import DatagridDisplay from "../../Components/DatagridDisplay";
import DisplayAlert from "../../Components/DisplayAlert";
import MegaLoader from "../../Components/MegaLoader";
import { Endpoints } from "../../Lib/endpoints";
import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
  generateRandomString,
  getEllipsisFromString,
  showResponseErrors,
  validateEmail,
} from "../../Lib/methods";
import { PaginatedResponse } from "../../Lib/responses";
import { PerformRequest, usePerformRequest } from "../../Lib/usePerformRequest";
import { AppContext } from "../Dashboard";

import "./styles.scss";

const selectProps: SelectProps = {
  allowDeselect: false,
};
const gridItemProps: GridProps = {
  size: {
    xl: 3,
    lg: 4,
    md: 4,
    sm: 6,
    xs: 12,
  },
};
const Teachers: React.FC = () => {
  const context = useContext(AppContext);
  const [pagination, setPagination] = useState<PaginationProps>({
    page: 1,
    limit: 20,
  });

  const filterForm = useForm({
    initialValues: {
      isActive: "all",
    },
    validate: {
      isActive: (value) =>
        value && value.length > 0 ? null : "Please select an active status",
    },
  });
  const {
    data: teachers,
    reloadData: reloadTeachers,
    isLoading: isLoadingTeam,
  } = usePerformRequest<PaginatedResponse<TeacherProps[]>>({
    route: Endpoints.GetTeachers(pagination.page, pagination.limit).route,
    method: Endpoints.GetTeachers(pagination.page, pagination.limit).method,
    body:
      filterForm.values.isActive === "all"
        ? {}
        : {
            isActive: filterForm.values.isActive,
          },
  });
  useEffect(() => {
    reloadTeachers();
  }, [filterForm.values.isActive]);

  const teacherForm = useForm({
    initialValues: {
      id: "",
      name: "",
      email: "",
      phone: "",
    },
    validate: {
      name: (value) => (value && value.length > 4 ? null : "Please enter name"),
      phone: (value) => (value && value.length === 11 ? null : "Invalid phone"),
      email: (value) =>
        validateEmail(value, true) ? null : "Enter a valid email",
    },
  });

  const [viewingTeacher, setViewingTeacher] = useState<TeacherProps>();

  const [teacherModalAction, setTeacherModalAction] = useState<
    "create" | "update" | "view"
  >("create");

  const [
    isTeacherModalOpened,
    { open: OpenTeacherModal, close: CloseTeacherModal },
  ] = useDisclosure(false);

  const [isCreatingOrUpdatingProfile, setCreatingOrUpdatingProfile] =
    useState<boolean>(false);
  const CreateNewTeacher = async () => {
    const { hasErrors: formHasErrors } = teacherForm.validate();
    // @ts-ignore
    const { name, phone, email } = teacherForm.values;

    if (!formHasErrors) {
      setCreatingOrUpdatingProfile(true);
      const r = await PerformRequest({
        method: Endpoints.GetCreateTeacher().method,
        route: Endpoints.GetCreateTeacher().route,
        data: {
          name,
          email,
          phone,
        },
        callback: () => {
          setCreatingOrUpdatingProfile(false);
        },
      });
      setCreatingOrUpdatingProfile(false);
      if (r) {
        if (r.status === HttpSuccessCodesObj.Created) {
          CancelCreateTeacher();
          reloadTeachers();
          DefaultSuccessNotification("Teacher created successfully!");
        } else if (r.status === HttpErrorCodesObj.Conflict) {
          DefaultErrorNotification("Teacher already exists!");
        }
      }
    }
  };
  const UpdateTeacher = async () => {
    const { hasErrors: formHasErrors } = teacherForm.validate();
    // @ts-ignore
    const { id, name, phone, email } = teacherForm.values;

    if (!formHasErrors) {
      setCreatingOrUpdatingProfile(true);
      const r = await PerformRequest({
        method: Endpoints.GetUpdateTeamMemberProfile(id).method,
        route: Endpoints.GetUpdateTeamMemberProfile(id).route,
        data: {
          name,
          email,
          phone,
        },
        callback: () => {
          setCreatingOrUpdatingProfile(false);
        },
      });
      setCreatingOrUpdatingProfile(false);
      if (r) {
        if ([409].includes(r.status)) {
          showResponseErrors(r);
        }
        if (r.status === 200) {
          CancelCreateTeacher();
          reloadTeachers();
          DefaultSuccessNotification("Profile has been updated!");
        }
      }
    }
  };

  const [deletingMemberID, setDeletingMemberID] = useState<string>("");
  const DeleteMember = async (id: string) => {
    // @ts-ignore
    setDeletingMemberID(id);
    const r = await PerformRequest({
      method: Endpoints.GetDeleteTeamMemberProfile(id).method,
      route: Endpoints.GetDeleteTeamMemberProfile(id).route,

      callback: () => {
        setCreatingOrUpdatingProfile(false);
        setDeletingMemberID("");
      },
    });
    setDeletingMemberID("");
    setCreatingOrUpdatingProfile(false);
    if (r) {
      if (r.status === 200) {
        CancelCreateTeacher();
        reloadTeachers();
        DefaultSuccessNotification("Profile has been REMOVED!");
      }
    }
  };

  const CancelCreateTeacher = () => {
    teacherForm.reset();
    setDeletingMemberID("");
    CloseTeacherModal();
  };
  const teacherFormInputProps = {
    my: 5,
  };

  const textProps: TextProps = {
    fz: 13,
    className: "table-value",
  };
  return (
    <Container maxWidth="lg">
      <div className="teachers-container">
        <div className="component-header flex-row align-end justify-between">
          <Text className="label">Teachers</Text>
          <div className="flex-row align-center">
            <Button
              color={Colors.PurplePrimary}
              rightSection={<IconPlus size={16} />}
              mr={10}
              onClick={() => {
                OpenTeacherModal();
                setTeacherModalAction("create");
                teacherForm.reset();
              }}
              disabled={
                isCreatingOrUpdatingProfile ||
                isLoadingTeam ||
                context?.profile?.role !== "superadmin"
              }
            >
              Add Profile
            </Button>
            <Button
              color={Colors.PurplePrimary}
              variant="outline"
              onClick={reloadTeachers}
              loading={isLoadingTeam}
              rightSection={<IconReload size={16} />}
            >
              Reload
            </Button>
          </div>
        </div>
        <Divider w="100%" my={10} />
        <Select
          w={200}
          {...filterForm.getInputProps("isActive")}
          {...selectProps}
          label="Filter by status"
          data={[
            ...YesOrNoList.map((item) => {
              return {
                value: item.value,
                label: item.alternativeLabel,
              };
            }),
            {
              value: "all",
              label: "All",
            },
          ]}
        />

        <div className="teachers">
          {isLoadingTeam ? (
            <MegaLoader
              size={50}
              containerStyle={{
                marginTop: 45,
                marginBottom: 45,
              }}
            />
          ) : (
            <>
              {!teachers?.data || teachers?.data.length === 0 ? (
                <DisplayAlert
                  title="No Teachers Found"
                  children={<Text>No teachers found!</Text>}
                />
              ) : (
                <DatagridDisplay
                  rows={teachers?.data.filter(
                    (t) => t.id !== context?.profile?.id
                  )}
                  isLoading={isLoadingTeam}
                  columns={[
                    {
                      field: "name",
                      flex: 1,
                      headerName: "Name",
                      renderCell: (
                        params: GridRenderCellParams<TeacherProps>
                      ) => (
                        <div className="flex-row flex-1 align-center">
                          <Text {...textProps}>{params.row.name}</Text>
                        </div>
                      ),
                    },
                    {
                      field: "email",
                      flex: 1,
                      headerName: "Email",
                      renderCell: (
                        params: GridRenderCellParams<TeacherProps>
                      ) => (
                        <div className="flex-row flex-1 align-center">
                          <CopyButton value={params.row.email}>
                            {({ copied, copy }) => (
                              <Text
                                fw={500}
                                {...textProps}
                                className="pointer"
                                onClick={copy}
                                style={{
                                  color: copied
                                    ? Colors.GreenPrimary
                                    : Colors.BluePrimary,
                                }}
                              >
                                {params.row.email}
                              </Text>
                            )}
                          </CopyButton>
                        </div>
                      ),
                    },
                    {
                      field: "phone",
                      flex: 1,
                      headerName: "Phone",
                      renderCell: (
                        params: GridRenderCellParams<TeacherProps>
                      ) => (
                        <div className="flex-row flex-1 align-center">
                          <CopyButton value={params.row.phone}>
                            {({ copied, copy }) => (
                              <Text
                                fw={500}
                                {...textProps}
                                className="pointer"
                                onClick={copy}
                                style={{
                                  color: copied
                                    ? Colors.GreenPrimary
                                    : Colors.BluePrimary,
                                }}
                              >
                                {params.row.phone}
                              </Text>
                            )}
                          </CopyButton>
                        </div>
                      ),
                    },
                    {
                      field: "isActive",
                      flex: 1,
                      headerName: "Status",
                      renderCell: (
                        params: GridRenderCellParams<TeacherProps>
                      ) => (
                        <div className="flex-row flex-1 align-center">
                          <Text
                            {...textProps}
                            tt="capitalize"
                            c={
                              params.row.isActive
                                ? Colors.GreenPrimary
                                : Colors.RedPrimary
                            }
                          >
                            {params.row.isActive ? "Active" : "Inactive"}
                          </Text>
                        </div>
                      ),
                    },

                    {
                      headerName: "Actions",
                      field: "id",
                      flex: 1,
                      renderCell: (
                        params: GridRenderCellParams<TeacherProps>
                      ) => (
                        <div className="flex-row flex-1 align-center">
                          <div className="flex-row align-center">
                            <ActionIcon
                              disabled={context?.profile?.role !== "superadmin"}
                              variant="subtle"
                              onClick={() => {
                                OpenTeacherModal();
                                setTeacherModalAction("update");
                                // @ts-ignore
                                teacherForm.setValues({
                                  id: params.row.id,
                                  name: params.row.name,
                                  phone: params.row.phone,
                                  email: params.row.email,
                                });
                              }}
                            >
                              <IconEdit size={18} />
                            </ActionIcon>
                            &nbsp; &nbsp;
                            <ActionIcon
                              disabled={context?.profile?.role !== "superadmin"}
                              variant="subtle"
                              onClick={() => {
                                OpenTeacherModal();
                                setTeacherModalAction("view");

                                setViewingTeacher(params.row);
                              }}
                            >
                              <IconEye size={18} />
                            </ActionIcon>
                            &nbsp; &nbsp;
                            <Popover
                              width={200}
                              position="bottom"
                              withArrow
                              shadow="md"
                            >
                              <Popover.Target>
                                <ActionIcon
                                  disabled={
                                    context?.profile?.role !== "superadmin"
                                  }
                                  variant="subtle"
                                  color={Colors.RedPrimary}
                                >
                                  <IconTrash size={18} />
                                </ActionIcon>
                              </Popover.Target>
                              <Popover.Dropdown>
                                <Text size="xs" c={Colors.RedPrimary}>
                                  This action CANNOT be undone!
                                </Text>
                                <Button
                                  color={Colors.RedPrimary}
                                  mt={5}
                                  w="100%"
                                  onClick={() => {
                                    DeleteMember(params.row.id);
                                  }}
                                  loading={deletingMemberID === params.row.id}
                                  disabled={
                                    deletingMemberID.length > 0 &&
                                    deletingMemberID !== params.row.id
                                  }
                                >
                                  Delete
                                </Button>
                              </Popover.Dropdown>
                            </Popover>
                          </div>
                        </div>
                      ),
                    },
                  ]}
                />
              )}
            </>
          )}
        </div>
      </div>
      <Modal
        opened={isTeacherModalOpened}
        onClose={CancelCreateTeacher}
        centered
        size="lg"
        withCloseButton={false}
      >
        <div className="plan-modal flex-col">
          <div className="flex-row align-center width-100 justify-between">
            <div className="flex-row align-center">
              <Text tt="capitalize" c={Colors.BluePrimary} fw={500} fz={17}>
                {teacherModalAction} Teacher
              </Text>
            </div>
            <ActionIcon
              variant="outline"
              color={Colors.BluePrimary}
              onClick={CancelCreateTeacher}
            >
              <IconX color={Colors.BluePrimary} size={18} />
            </ActionIcon>
          </div>
          {teacherModalAction === "view" ? (
            <>
              {viewingTeacher && (
                <>
                  <Table
                    mt={20}
                    variant="vertical"
                    layout="fixed"
                    withTableBorder
                  >
                    <Table.Thead>
                      <Table.Tr key={generateRandomString(12)}>
                        <Table.Th w={200}>Email</Table.Th>
                        <Table.Td>
                          <CopyButton value={viewingTeacher.email}>
                            {({ copied, copy }) => (
                              <Tooltip
                                label={copied ? "Copied!" : "Copy to clipboard"}
                              >
                                <div
                                  className="pointer flex-row align-center"
                                  onClick={copy}
                                  style={{
                                    color: copied
                                      ? Colors.PurplePrimary
                                      : Colors.BluePrimary,
                                    height: "100%",
                                  }}
                                >
                                  {getEllipsisFromString(
                                    viewingTeacher.email,
                                    20
                                  )}
                                </div>
                              </Tooltip>
                            )}
                          </CopyButton>
                        </Table.Td>
                      </Table.Tr>
                      <Table.Tr key={generateRandomString(12)}>
                        <Table.Th w={200}>Phone</Table.Th>
                        <Table.Td>
                          <CopyButton value={viewingTeacher.phone}>
                            {({ copied, copy }) => (
                              <Tooltip
                                label={copied ? "Copied!" : "Copy to clipboard"}
                              >
                                <div
                                  className="pointer flex-row align-center"
                                  onClick={copy}
                                  style={{
                                    color: copied
                                      ? Colors.PurplePrimary
                                      : Colors.BluePrimary,
                                    height: "100%",
                                  }}
                                >
                                  {getEllipsisFromString(
                                    viewingTeacher.phone,
                                    14
                                  )}
                                </div>
                              </Tooltip>
                            )}
                          </CopyButton>
                        </Table.Td>
                      </Table.Tr>

                      <Table.Tr key={generateRandomString(12)}>
                        <Table.Th>Name</Table.Th>
                        <Table.Td>{viewingTeacher.name}</Table.Td>
                      </Table.Tr>

                      <Table.Tr key={generateRandomString(12)}>
                        <Table.Th>Creation Date</Table.Th>
                        <Table.Td>
                          {dayjs(viewingTeacher.createdAt).format(
                            "ddd, MMMM DD YYYY"
                          )}
                        </Table.Td>
                      </Table.Tr>
                    </Table.Thead>
                  </Table>
                </>
              )}
            </>
          ) : (
            <>
              <div className="flex-row align-center justify-between width-100">
                <TextInput
                  {...teacherForm.getInputProps("email")}
                  {...teacherFormInputProps}
                  disabled={teacherModalAction === "update"}
                  label="Email"
                  placeholder={`email@${AppDomain}`}
                  spellCheck={false}
                  w="48%"
                  leftSection={<IconAt size={16} />}
                />
                <TextInput
                  {...teacherForm.getInputProps("phone")}
                  {...teacherFormInputProps}
                  w="48%"
                  label="Phone"
                  placeholder="0802******"
                  spellCheck={false}
                  leftSection={<IconPhone size={16} />}
                />
              </div>
              <TextInput
                {...teacherForm.getInputProps("name")}
                {...teacherFormInputProps}
                w="100%"
                label="Name"
                placeholder="Full Name"
                spellCheck={false}
                leftSection={<IconUser size={16} />}
              />

              <div className="flex-row width-100 justify-between align-center">
                <Button
                  mt={10}
                  w="48%"
                  color={Colors.PurplePrimary}
                  onClick={
                    teacherModalAction === "create"
                      ? CreateNewTeacher
                      : UpdateTeacher
                  }
                  loading={isCreatingOrUpdatingProfile}
                  tt="capitalize"
                >
                  {teacherModalAction}
                </Button>
                <Button
                  mt={10}
                  w="48%"
                  variant="outline"
                  onClick={CancelCreateTeacher}
                  disabled={isCreatingOrUpdatingProfile}
                  color={Colors.RedPrimary}
                >
                  Cancel
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>
    </Container>
  );
};

export default Teachers;
